import React, { useEffect, useRef, useCallback } from "react";
import { Star, ShootingStar, CanvasBackgroundProps } from "../../../types/background";

const Background: React.FC<CanvasBackgroundProps> = ({
  starDensity = 600,
  starColors = {},
  nebulaIntensity = 0.4,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const starsRef = useRef<Star[]>([]);
  const shootingStarsRef = useRef<ShootingStar[]>([]);

  const initStars = useCallback(
    (canvas: HTMLCanvasElement) => {
      const count = Math.floor((canvas.width * canvas.height) / starDensity);
      starsRef.current = Array.from({ length: count }).map(() => {
        const isBright = Math.random() > 0.85;
        return {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: isBright ? Math.random() * 1.2 + 0.8 : Math.random() * 0.6 + 0.2,
          baseOpacity: isBright ? 0.8 + Math.random() * 0.2 : 0.2 + Math.random() * 0.4,
          blinkSpeed: isBright ? 0.0005 + Math.random() * 0.001 : 0.002 + Math.random() * 0.003,
          phase: Math.random() * Math.PI * 2,
          color: isBright
            ? starColors.bright || "rgba(255, 240, 200, 0.95)"
            : starColors.main || "rgba(200, 220, 255, 0.8)",
        };
      });
    },
    [starColors, starDensity]
  );

  const drawStars = (ctx: CanvasRenderingContext2D, time: number) => {
    starsRef.current.forEach((star) => {
      const opacity =
        star.baseOpacity * (0.6 + 0.4 * Math.sin(time * star.blinkSpeed + star.phase));

      ctx.fillStyle = star.color;
      ctx.globalAlpha = opacity;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
      ctx.fill();

      if (opacity > 0.7) {
        ctx.globalAlpha = opacity * 0.3;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius * 2, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    ctx.globalAlpha = 1;
  };

  const drawShootingStars = useCallback(
    (ctx: CanvasRenderingContext2D) => {
      const now = performance.now();
      const shootingStars = shootingStarsRef.current;

      if (Math.random() > 0.995 && shootingStars.length < 2) {
        shootingStars.push({
          x: Math.random() * ctx.canvas.width,
          y: Math.random() * ctx.canvas.height * 0.3,
          length: 60 + Math.random() * 80,
          speed: 5 + Math.random() * 3,
          angle: 35 + Math.random() * 20,
          opacity: 1,
          startTime: now,
          duration: 1000 + Math.random() * 500,
          color: starColors.shooting || "rgba(150, 220, 255, 0.95)",
        });
      }

      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const star = shootingStars[i];
        const progress = (now - star.startTime) / star.duration;

        if (progress > 1) {
          shootingStars.splice(i, 1);
          continue;
        }

        const x =
          star.x + star.speed * star.duration * progress * Math.cos((star.angle * Math.PI) / 180);
        const y =
          star.y + star.speed * star.duration * progress * Math.sin((star.angle * Math.PI) / 180);

        ctx.save();
        ctx.strokeStyle = star.color;
        ctx.lineWidth = 2;
        ctx.globalAlpha = star.opacity * (1 - progress * 0.7);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(
          x - star.length * Math.cos((star.angle * Math.PI) / 180),
          y - star.length * Math.sin((star.angle * Math.PI) / 180)
        );
        ctx.stroke();

        ctx.globalAlpha = star.opacity * (1 - progress);
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    },
    [starColors.shooting]
  ); // 👈 зависимость

  const drawNebulaClouds = (ctx: CanvasRenderingContext2D, time: number) => {
    ctx.save();
    ctx.globalCompositeOperation = "lighter";

    const drawCloud = (x: number, y: number, size: number, hue: number, timeOffset: number) => {
      const alpha = 0.15 * (0.7 + 0.3 * Math.sin(time * 0.0005 + timeOffset));
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, size);
      gradient.addColorStop(0, `hsla(${hue}, 80%, 70%, ${alpha})`);
      gradient.addColorStop(1, `hsla(${hue}, 80%, 70%, 0)`);

      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    };

    const cloudSize = Math.max(ctx.canvas.width, ctx.canvas.height) * 0.8;
    drawCloud(
      ctx.canvas.width * 0.3 + Math.sin(time * 0.0003) * 100,
      ctx.canvas.height * 0.4,
      cloudSize,
      220,
      0
    );
    drawCloud(
      ctx.canvas.width * 0.7 + Math.cos(time * 0.0004) * 80,
      ctx.canvas.height * 0.6,
      cloudSize,
      260,
      2
    );
    drawCloud(
      ctx.canvas.width * 0.5 + Math.sin(time * 0.00035) * 120,
      ctx.canvas.height * 0.3,
      cloudSize,
      180,
      4
    );

    ctx.restore();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const init = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initStars(canvas);
    };

    const animate = (time: number) => {
      if (!ctx) return;

      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, "#1a0033");
      gradient.addColorStop(0.5, "#3b0a60");
      gradient.addColorStop(1, "#0a0c20");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawNebulaClouds(ctx, time);
      drawStars(ctx, time);
      drawShootingStars(ctx);

      animationRef.current = requestAnimationFrame(animate);
    };

    init();
    animationRef.current = requestAnimationFrame(animate);

    const handleResize = () => init();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [starDensity, starColors, nebulaIntensity, initStars, drawShootingStars]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: -1,
        pointerEvents: "none",
      }}
    />
  );
};

export default Background;
